import React, { useState } from 'react';
import { useToastStore } from '@store/useToastStore';
import { useImportController } from '@app/library/useImportController';
import type { BookMetadata } from '~types/book';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Loader2 } from 'lucide-react';
import { createLogger } from '@lib/logger';

const logger = createLogger('DeleteBookDialog');

interface DeleteBookDialogProps {
    isOpen: boolean;
    onClose: () => void;
    book: BookMetadata | null;
}

export const DeleteBookDialog: React.FC<DeleteBookDialogProps> = ({ isOpen, onClose, book }) => {
    const { removeBook } = useImportController();
    const showToast = useToastStore(state => state.showToast);
    const [isDeleting, setIsDeleting] = useState(false);

    if (!book) return null;

    const confirmDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isDeleting) return;
        setIsDeleting(true);

        try {
            await removeBook(book.id);
            showToast(`Deleted "${book.title}"`, 'success');
            onClose();
        } catch (error) {
            logger.error("Failed to delete book", error);
            showToast("Failed to delete book", "error");
        } finally {
             // Re-enable the controls on both paths. After a successful delete the
             // dialog has already closed, so the reset is harmless; after an error
             // it stays open so the user can retry.
             setIsDeleting(false);
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={() => {
                if (!isDeleting) onClose();
            }}
            title="Delete Book"
            description={`Are you sure you want to delete "${book.title}"? This cannot be undone.`}
            footer={
                <>
                    <Button
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        disabled={isDeleting}
                        aria-label={isDeleting ? "Cancel (disabled while deleting)" : "Cancel"}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={confirmDelete}
                        disabled={isDeleting}
                        data-testid="confirm-delete"
                        className="gap-2"
                    >
                        {isDeleting && (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                <span className="sr-only" aria-live="polite">Deleting...</span>
                            </>
                        )}
                        <span aria-hidden={isDeleting}>{isDeleting ? "Deleting..." : "Delete"}</span>
                    </Button>
                </>
            }
        />
    );
};
